/* eslint-disable import/no-named-as-default */
/* eslint-disable @typescript-eslint/ban-ts-comment */
import React, { useState, forwardRef, ForwardedRef, useCallback, Ref } from 'react';

import cx from 'classnames';

import LoginLoader from 'components/loader/loader';

// eslint-disable-next-line @typescript-eslint/no-unused-vars
import classes from './deltaTable.module.scss';
import DeltaTableProvider from './DeltaTableProvider';
import { TableProps, SortOrder, RowClick } from './interface';
import TableHeader from './TableHeader';
import TableRows from './TableRows';

/**
 * @summary Custom Table with expandable rows
 *
 * @typedef TableProps
 * @param loading {boolean} - show loader
 * @param data {Array<Record<string, any>>} - "Memoized" table data
 * @param onRowClick {RowClick} ```onRowClick?:
 *   | ((
 *       event: SyntheticEvent<Element>,
 *       record: Record<string, any>,
 *       rowIndex: number | string,
 *   ) => void)
 *   | undefined;```
 *  @param onSortChange```(sortObj: { sortOrder: SortOrder; activeSortKey: string | undefined }) => void```
 *  @param dataIndex the key that is returned when sort/click functions are called
 *  @param sortDirections Array<"asc" | "desc">
 *  @param columns {Array<Columns>} - "Memoized" table columns
 *  @param emptyComponent {React.ReactNode} - component to show when table is empty
 *  @param expandableRowsComponent {React.ReactNode} - component to show when table is expanded
 *  @param showFooter {boolean} - show footer content for each column - columnFooterComponent in Columns required if showFooter is true
 *  @param allowAccordionExpand {boolean} - allow any row to be expanded at once
 *  @param columnResize {boolean} - allow resizing of columns
 */
const DeltaTable = forwardRef(
  <T,>(
    {
      data,
      columns,
      loading,
      fixedHeader,
      emptyComponent,
      responsive,
      sortDirections,
      className,
      bodyRowClass,
      headerRowClass,
      footerRowClass,
      tableContainerClass,
      emptyTableClass,
      expandedRowGlobalClass,
      height,
      width,
      minWidth,
      showFooter,
      onRowClick,
      onSortChange,
      expandableRowsComponent,
      allowAccordionExpand,
      autoSizeAllColumns,
      resizeColumnsToFit,
      columnResize,
      onRowMouseEnter,
      onRowMouseLeave,
      onScroll,
      virtualOverscan = 5,
      autoScroll = {
        reverseScroll: false,
        debounced: false,
        visibilityChange: false,
        scrollOnChange: '',
      },
      positionAbsolute,
      'data-testid': tid,
      rowHeight,
      showBody = true,
      tbodyStartElement,
      tBodyEndElement,
    }: TableProps<T>,
    ref: ForwardedRef<HTMLTableElement>
  ): JSX.Element => {
    const [sortOrder, setSortOrder] = useState<SortOrder>(undefined);
    const [parentRef, setParentRef] = useState({ current: null });

    const parentCallback = useCallback(node => {
      setParentRef({ current: node });
    }, []);

    // causing too many unnecessary consoles
    // if (fixedHeader && !height) {
    //   console.warn('DeltaTable: height is required for fixedHeader to work');
    // }

    const showEmptyComponent = !loading && data && data.length === 0;

    return (
      (<div
        className={cx(classes.table, {
          [`${classes.responsive}`]: responsive,
          [`${tableContainerClass}`]: tableContainerClass,
          [`${classes.showScrollbar}`]: !String(window?.navigator?.platform)
            .toLowerCase()
            ?.includes('mac'),
        })}
        style={{
          height,
          width,
          minWidth,
          ...(showEmptyComponent && { overflowX: 'hidden' }),
        }}
        onScroll={onScroll || undefined}
        ref={parentCallback}
        data-palette="DeltaTable">
        {loading && (
          <div className={classes.loader} data-palette="DeltaTable">
            <LoginLoader />
          </div>
        )}
        {showEmptyComponent && (
          <div className={cx(classes.empty, emptyTableClass)} data-palette="DeltaTable">
            {emptyComponent || <p data-palette="DeltaTable">No items found</p>}
          </div>
        )}
        {!loading && data && data.length ? (
          <DeltaTableProvider<T>
            data={data}
            columns={columns}
            sortOrder={sortOrder}
            autoSizeAllColumns={autoSizeAllColumns}
            resizeColumnsToFit={resizeColumnsToFit}
          >
            <table
              ref={ref}
              data-testid={tid}
              className={cx({
                [`${className}`]: className,
                [`${classes.fixed}`]: fixedHeader && !positionAbsolute,
                [classes.positionAbsolute]: fixedHeader && positionAbsolute,
              })}
              role="grid"
            >
              <TableHeader
                onSortChange={onSortChange}
                rowClass={headerRowClass}
                sortDirections={sortDirections}
                fixedHeader={fixedHeader}
                setSortOrder={setSortOrder}
                columnResize={columnResize}
              />

              {tbodyStartElement && (
                <tbody data-palette="DeltaTable">
                  <tr>
                    <td colSpan={columns.length}>{tbodyStartElement}</td>
                  </tr>
                </tbody>
              )}

              <TableRows
                rowHeight={rowHeight}
                showFooter={showFooter}
                rowClass={bodyRowClass}
                footerRowClass={footerRowClass}
                expandedRowGlobalClass={expandedRowGlobalClass}
                allowAccordionExpand={allowAccordionExpand}
                // TODO: Fix 'any' type!
                onRowClick={onRowClick as RowClick<any>}
                expandableRowsComponent={expandableRowsComponent as any}
                onRowMouseEnter={onRowMouseEnter as RowClick<any>}
                onRowMouseLeave={onRowMouseLeave as RowClick<any>}
                parentRef={parentRef}
                virtualOverscan={virtualOverscan}
                showBody={showBody}
                autoScroll={autoScroll}
              />

              {tBodyEndElement && (
                <tbody data-palette="DeltaTable">
                  <tr>
                    <td colSpan={columns.length}>{tBodyEndElement}</td>
                  </tr>
                </tbody>
              )}
            </table>
          </DeltaTableProvider>
        ) : null}
      </div>)
    );
  }
) as <T>(p: TableProps<T> & { ref?: Ref<HTMLTableElement> }) => JSX.Element;

export default DeltaTable;
